import { modifyElkDataObject } from "./elk-utils.js";
import { Direction } from "../../util/utils.js";
import { DefaultAlgorithmConfiguration, LayeredConfiguration, RadialConfiguration, StressConfiguration } from "../algorithm-configurations.js";
/**
 * Stores configuration for elk layered algorithm
 */
export class ElkLayeredConfiguration extends LayeredConfiguration {
    static getDefaultUserConfiguration() {
        return {
            layout_alg: "elk_layered",
            alg_direction: Direction.Up,
            layer_gap: 200,
            in_layer_gap: 100,
            edge_routing: "ORTHOGONAL",
            advanced_settings: {},
            run_layered_after: false,
            run_node_overlap_removal_after: false,
            interactive: false
        };
    }
    constructor(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall) {
        super(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall);
        this.algorithmName = "elk_layered";
        // Hardcoded defaults
        this.elkData["elk.edgeRouting"] = "SPLINES";
        this.elkData["spacing.edgeEdge"] = "10";
        this.elkData["org.eclipse.elk.spacing.nodeSelfLoop"] = "150";
        modifyElkDataObject(this.userGivenConfiguration, this.elkData);
    }
    addAdvancedSettingsToUnderlyingData(advancedSettings) {
        modifyElkDataObject({ "advanced_settings": advancedSettings }, this.elkData);
    }
    addAlgorithmConfigurationToUnderlyingData(key, value) {
        modifyElkDataObject({ [key]: value, "layout_alg": this.algorithmName }, this.elkData);
    }
    elkData = {};
}
/**
 * Stores configuration for elk stress algorithm
 */
export class ElkStressConfiguration extends StressConfiguration {
    static getDefaultUserConfiguration() {
        return {
            layout_alg: "elk_stress",
            stress_edge_len: 800,
            number_of_new_algorithm_runs: 10,
            advanced_settings: {},
            run_layered_after: false,
            run_node_overlap_removal_after: true,
            interactive: false
        };
    }
    constructor(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall) {
        super(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall);
        this.algorithmName = "elk_stress";
        modifyElkDataObject(this.userGivenConfiguration, this.elkData);
    }
    addAdvancedSettingsToUnderlyingData(advancedSettings) {
        modifyElkDataObject({ "advanced_settings": advancedSettings }, this.elkData);
    }
    addAlgorithmConfigurationToUnderlyingData(key, value) {
        modifyElkDataObject({ [key]: value, "layout_alg": this.algorithmName }, this.elkData);
    }
    elkData = {};
}
export class ElkStressAdvancedUsingClustersConfiguration extends StressConfiguration {
    static getDefaultUserConfiguration() {
        return {
            layout_alg: "elk_stress_advanced_using_clusters",
            stress_edge_len: 800,
            number_of_new_algorithm_runs: 10,
            advanced_settings: {},
            run_layered_after: false,
            run_node_overlap_removal_after: true,
            interactive: false
        };
    }
    constructor(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall) {
        super(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall);
        this.algorithmName = "elk_stress_advanced_using_clusters";
        modifyElkDataObject(this.userGivenConfiguration, this.elkData);
    }
    addAdvancedSettingsToUnderlyingData(advancedSettings) {
        modifyElkDataObject({ "advanced_settings": advancedSettings }, this.elkData);
    }
    addAlgorithmConfigurationToUnderlyingData(key, value) {
        modifyElkDataObject({ [key]: value, "layout_alg": this.algorithmName }, this.elkData);
    }
    elkData = {};
}
export class ElkStressProfileLayoutConfiguration extends StressConfiguration {
    static getDefaultUserConfiguration() {
        return {
            layout_alg: "elk_stress_profile",
            profileEdgeLength: 400,
            preferredProfileDirection: Direction.Up,
            stress_edge_len: 800,
            number_of_new_algorithm_runs: 10,
            advanced_settings: {},
            run_layered_after: false,
            run_node_overlap_removal_after: true,
            interactive: false
        };
    }
    constructor(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall) {
        super(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall);
        this.algorithmName = "elk_stress_profile";
        modifyElkDataObject(this.userGivenConfiguration, this.elkData);
    }
    addAdvancedSettingsToUnderlyingData(advancedSettings) {
        modifyElkDataObject({ "advanced_settings": advancedSettings }, this.elkData);
    }
    addAlgorithmConfigurationToUnderlyingData(key, value) {
        modifyElkDataObject({ [key]: value, "layout_alg": this.algorithmName }, this.elkData);
    }
    elkData = {};
}
/**
 * Stores configuration for elk force algorithm
 */
export class ElkForceConfiguration extends DefaultAlgorithmConfiguration {
    static getDefaultUserConfiguration() {
        return {
            layout_alg: "elk_force",
            min_distance_between_nodes: 400,
            force_alg_type: "FRUCHTERMAN_REINGOLD",
            number_of_new_algorithm_runs: 10,
            advanced_settings: {},
            run_layered_after: false,
            run_node_overlap_removal_after: true,
            interactive: false
        };
    }
    constructor(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall) {
        super(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall);
        this.algorithmName = "elk_force";
        modifyElkDataObject(this.userGivenConfiguration, this.elkData);
        // Hardcoded ... we have decided to not use force algorithm anyways
        if (this.elkData["org.eclipse.elk.force.model"] === "EADES") {
            this.elkData["org.eclipse.elk.force.repulsion"] = "25.0";
        }
        else {
            this.elkData["elk.force.temperature"] = "0.1";
        }
        // Random seed == 0 means that the seed is chosen randomly. Seed sets the initial position of nodes
        this.elkData["org.eclipse.elk.randomSeed"] = "0";
    }
    addAdvancedSettingsToUnderlyingData(advancedSettings) {
        modifyElkDataObject({ "advanced_settings": advancedSettings }, this.elkData);
    }
    addAlgorithmConfigurationToUnderlyingData(key, value) {
        modifyElkDataObject({ [key]: value, "layout_alg": this.algorithmName }, this.elkData);
    }
    // I can further enforce the typing by creating type which takes into consideration only used parameters and not those which can't be used
    // (For example those for layered algorithm), but I won't
    elkData = {};
}
/**
 * Stores configuration for elk radial algorithm
 */
export class ElkRadialConfiguration extends RadialConfiguration {
    static getDefaultUserConfiguration() {
        return {
            layout_alg: "elk_radial",
            min_distance_between_nodes: 500,
            advanced_settings: {},
            run_layered_after: false,
            run_node_overlap_removal_after: false,
            interactive: false
        };
    }
    constructor(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall) {
        super(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall);
        this.algorithmName = "elk_radial";
        modifyElkDataObject(this.userGivenConfiguration, this.elkData);
    }
    addAdvancedSettingsToUnderlyingData(advancedSettings) {
        modifyElkDataObject({ "advanced_settings": advancedSettings }, this.elkData);
    }
    addAlgorithmConfigurationToUnderlyingData(key, value) {
        modifyElkDataObject({ [key]: value, "layout_alg": this.algorithmName }, this.elkData);
    }
    elkData = {};
}
/**
 * Stores configuration for elk sporeOverlap algorithm
 */
export class ElkSporeOverlapConfiguration extends DefaultAlgorithmConfiguration {
    static getDefaultUserConfiguration() {
        return {
            layout_alg: "elk_overlapRemoval",
            min_distance_between_nodes: 200,
            advanced_settings: {},
            run_layered_after: false,
            run_node_overlap_removal_after: false,
            interactive: true
        };
    }
    constructor(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall) {
        super(userGivenConfiguration, affectedNodes, shouldCreateNewGraph, algorithmPhasesToCall);
        this.algorithmName = "elk_overlapRemoval";
        modifyElkDataObject(this.userGivenConfiguration, this.elkData);
    }
    addAdvancedSettingsToUnderlyingData(advancedSettings) {
        modifyElkDataObject({ "advanced_settings": advancedSettings }, this.elkData);
    }
    addAlgorithmConfigurationToUnderlyingData(key, value) {
        modifyElkDataObject({ [key]: value, "layout_alg": this.algorithmName }, this.elkData);
    }
    elkData = {};
}
//# sourceMappingURL=elk-configurations.js.map